import { Link } from 'react-router-dom';
import {
  Users,
  Map as MapIcon,
  ListPlus,
  Layers,
  ArrowRight,
} from 'lucide-react';

import { Section } from '@/components/primitives/Section';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

interface MembersParcelsTabProps {
  tenantId: number;
}

/**
 * Lightweight quick-access tab pointing at the existing Members / Parcels
 * pages. We intentionally don't embed those screens here — they're big
 * pages of their own (member CRUD + estate management + the spatial
 * parcel-link map) and stuffing them into a tab would be slower without
 * adding value. The Hub stays the front door; deeper work lives in its
 * own routes that remember their scroll position and tab state.
 */
export default function MembersParcelsTab({
  tenantId,
}: MembersParcelsTabProps) {
  return (
    <div className="space-y-4">
      <Section
        title="Members"
        description="FPA members and the parcels they own."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Shortcut
            icon={Users}
            title="Member list"
            description="Full list across this FPA, with search, filters, and bulk actions."
            to="/members"
          />
          <Shortcut
            icon={ListPlus}
            title="Add member"
            description="Create a new FPA member record."
            to="/members?new=1"
          />
        </div>
      </Section>

      <Section
        title="Parcels"
        description="Spatial view of parcels and member links."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Shortcut
            icon={MapIcon}
            title="Parcels map"
            description="View parcels coloured by member hazard tags. Link members to parcels by clicking polygons."
            to={`/admin/tenants/${tenantId}/parcels-map`}
          />
          <Shortcut
            icon={Layers}
            title="Parcel pipeline"
            description="Upload source data and reindex. Admin only."
            to="/members/parcels"
          />
        </div>
        <Alert className="mt-3">
          <AlertDescription>
            Parcel geometry is shared across all FPAs from a single source.
            Reassigning parcels to this FPA happens via{' '}
            <Link to="?tab=boundaries" className="underline">
              Boundaries → Reindex
            </Link>
            .
          </AlertDescription>
        </Alert>
      </Section>
    </div>
  );
}

interface ShortcutProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  to: string;
  className?: string;
}

function Shortcut({ icon: Icon, title, description, to, className }: ShortcutProps) {
  return (
    <Link
      to={to}
      className={cn(
        'flex items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/60',
        className,
      )}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary/10 text-secondary">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="font-medium leading-tight">{title}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </Link>
  );
}
